import styled from '@emotion/styled'
import React, { useCallback, useMemo, useState, useRef } from 'react'
import { Card } from 'react-bootstrap'
import Button from '../../../components/common/Button'
import { Authorize } from '../../../components/Authorize'
import { ROLE } from '../../../services/user/user-typed'
import { Field, Form } from 'react-final-form'
import { cp, createValidation, maxLength, required, when } from '../../../../utils/field-validation'
import Text from '../../../components/common/Text'
import ConfirmCard from '../../../components/common/Card'
import { CheckBoxField, InputField, DatePickerField } from '../../../components/fields'
import { FormControlLabel, makeStyles, Modal } from '@material-ui/core'
import {
  useCreateTopDeal,
  useGetTopDeal,
  useUpdateTopDeal,
} from '../../../services/top-deal/top-deal-query'
import { useToastForReactQuery, useVisible } from '../../../../utils/custom-hooks'
import { useTranslation } from 'react-i18next'
import { FormApi } from 'final-form'
import dayjs from 'dayjs'

const Layout = styled.div`
  display: flex;
  flex: 1;
  flex-flow: row;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  padding-right: 16px;
`

const ButtonStyled = styled(Button)`
  padding: 7px 13px;
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px;
`

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

type FormValue = {
  name: string
  description: string
  status: boolean
  promoCode?: string
  publishDate: string
  endDate: string
}

const validate = createValidation<FormValue>(values => ({
  name: cp(required('required top deal'), maxLength(50, 'Maximum length required 50')),
  description: cp(required('required description'), maxLength(100, 'Maximum length required 100')),
  promoCode: cp(maxLength(50, 'Maximum length required 50')),
  publishDate: cp(required('required publish date')),
  endDate: cp(
    required('required end date'),
    when(
      () => {
        return dayjs(values.endDate) < dayjs(values.publishDate)
      },
      () => 'End date must greater than publish date',
    ),
  ),
}))

export const TopDeal = () => {
  const [isViewMode, setIsViewMode] = useState(true)
  const toast = useToastForReactQuery()
  const { t } = useTranslation()
  const onConfirmModalVisible = useVisible()
  const [isDisabled, setIsDisabled] = useState(true)
  const classes = useStyles()
  const formRef = useRef<FormApi<FormValue, Partial<FormValue>>>()
  const { data: topDeal, isLoading } = useGetTopDeal()
  const idTopDeal = useMemo(() => {
    return !isLoading && topDeal ? topDeal?.items?.[0]?.id : ''
  }, [isLoading, topDeal])
  const { mutate: updateTopDeal } = useUpdateTopDeal(idTopDeal)
  const { mutate: createTopDeal } = useCreateTopDeal()

  const handleOpenConfirmModal = useCallback(() => {
    onConfirmModalVisible.open()
  }, [onConfirmModalVisible])

  const handleCloseConfirmModal = useCallback(() => {
    onConfirmModalVisible.close()
  }, [onConfirmModalVisible])

  const initialValues = useMemo(() => {
    const item = topDeal?.items?.[0]
    return {
      name: item?.name || '',
      description: item?.description || '',
      status: item?.status || false,
      promoCode: item?.promoCode || '',
      publishDate: item?.publishDate ? dayjs(item.publishDate).format('YYYY-MM-DDTHH:mm') : '',
      endDate: item?.endDate ? dayjs(item.endDate).format('YYYY-MM-DDTHH:mm') : '',
    }
  }, [topDeal])

  const handleSubmit = useCallback(
    (value: FormValue) => {
      console.debug('value', value)
      const params = {
        ...value,
        publishDate: value.publishDate ? dayjs(value.publishDate).toISOString() : undefined,
        endDate: value.endDate ? dayjs(value.endDate).toISOString() : undefined,
      }
      if (idTopDeal) {
        updateTopDeal(
          params,
          {
            onSuccess: () => {
              toast.open('Success', {
                type: 'success',
                autoClose: 3 * 1000,
              })
              setIsViewMode(true)
              handleCloseConfirmModal()
            },
            onError: (error: any) => {
              toast.open(t(`${error}`), {
                type: 'error',
                autoClose: 3 * 1000,
              })
            },
          },
        )
      } else {
        createTopDeal(
          params,
          {
            onSuccess: () => {
              toast.open('Success', {
                type: 'success',
                autoClose: 3 * 1000,
              })
              setIsViewMode(true)
              handleCloseConfirmModal()
            },
            onError: (error: any) => {
              toast.open(t(`${error}`), {
                type: 'error',
                autoClose: 3 * 1000,
              })
            },
          },
        )
      }
    },
    [createTopDeal, handleCloseConfirmModal, idTopDeal, t, toast, updateTopDeal],
  )

  const handleSetting = useCallback(() => {
    setIsViewMode(false)
  }, [])

  const handleCancelSetting = useCallback(() => {
    setIsViewMode(true)

    if (formRef.current !== undefined) {
      formRef.current.reset()
    }
  }, [])

  return (
    <Card>
      <Card.Body>
        <Layout>
          <Authorize role={ROLE.MANAGER}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
              <Authorize role={ROLE.ADMIN}>
                {isViewMode && (
                  <ButtonStyled variant="warning" onClick={handleSetting}>
                    Edit
                  </ButtonStyled>
                )}
              </Authorize>

              {!isViewMode && (
                <>
                  <ButtonStyled
                    variant="warning"
                    disabled={isDisabled}
                    onClick={handleOpenConfirmModal}
                  >
                    Submit
                  </ButtonStyled>

                  <ButtonStyled
                    variant="warning"
                    style={{
                      backgroundColor: '#FF0000',
                      borderColor: '#FF0000',
                    }}
                    onClick={handleCancelSetting}
                  >
                    Cancel
                  </ButtonStyled>
                </>
              )}
            </div>
          </Authorize>
        </Layout>

        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
          render={({ handleSubmit, invalid, values, form }) => {
            if (formRef?.current === undefined) {
              formRef.current = form
            }

            setIsDisabled(invalid)
            return (
              <>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
                  <div style={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
                    <ContentLayout>
                      <div style={{ paddingBottom: '10px' }}>
                        <Text>Name</Text>
                        <Field
                          name="name"
                          component={InputField}
                          placeholder="name"
                          id="name"
                          disabled={isViewMode}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <div style={{ paddingBottom: '10px' }}>
                        <Text>Description</Text>
                        <Field
                          name="description"
                          component={InputField}
                          placeholder="description"
                          id="description"
                          disabled={isViewMode}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <div style={{ paddingBottom: '10px' }}>
                        <Text>Promo code</Text>
                        <Field
                          name="promoCode"
                          component={InputField}
                          placeholder="promo code"
                          id="promo code"
                          disabled={isViewMode}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <div style={{ paddingBottom: '10px' }}>
                        <Text>Publish Date</Text>
                        <Field
                          name="publishDate"
                          component={DatePickerField}
                          id="publishDate"
                          label=""
                          type="datetime-local"
                          disabled={isViewMode}
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </div>

                      <div style={{ paddingBottom: '10px' }}>
                        <Text>End Date</Text>
                        <Field
                          name="endDate"
                          component={DatePickerField}
                          id="endDate"
                          label=""
                          type="datetime-local"
                          disabled={isViewMode}
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </div>

                      <FormControlLabel
                        control={
                          <Field name="status" component={CheckBoxField} disabled={isViewMode} />
                        }
                        label="Active"
                      />
                    </ContentLayout>
                  </div>
                </div>

                <Modal
                  className={classes.modal}
                  open={onConfirmModalVisible.visible}
                  onClose={onConfirmModalVisible.close}
                >
                  <ConfirmCard
                    style={{
                      display: 'flex',
                      width: '300px',
                      padding: '30px',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Text>Confirm</Text>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignSelf: 'center',
                        paddingTop: '10px',
                      }}
                    >
                      <Button
                        style={{
                          alignSelf: 'center',
                          marginRight: '20px',
                        }}
                        onClick={handleSubmit}
                      >
                        Yes
                      </Button>
                      <Button
                        style={{
                          alignSelf: 'center',
                          backgroundColor: '#FF0000',
                          borderColor: '#FF0000',
                        }}
                        onClick={handleCloseConfirmModal}
                      >
                        No
                      </Button>
                    </div>
                  </ConfirmCard>
                </Modal>
              </>
            )
          }}
        />
      </Card.Body>
    </Card>
  )
}

export default TopDeal
